<template>
  <div class="map_wrapper">
    <input
      type="text"
      ref="searchInput"
      :placeholder="$t('PLACEHOLDERS.search_by_name')"
      class="map-search-input"
    />

    <GmapMap
      :center="mapCenter"
      :zoom="12"
      map-type-id="terrain"
      style="width: 100%; height: 400px"
      @click="onMapClick"
    >
      <GmapMarker
        :position="mapCenter"
        :clickable="true"
        :draggable="true"
        @dragend="onMarkerDragEnd"
      />
    </GmapMap>
  </div>
</template>

<script>
export default {
  name: "ChangableMapComponent",

  props: {
    lat: {
      type: Number,
      required: true,
    },
    lng: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      placeName: "",
      autocomplete: null,
    };
  },

  computed: {
    mapCenter() {
      return {
        lat: parseFloat(this.lat),
        lng: parseFloat(this.lng),
      };
    },
  },

  watch: {
    lat(newLat, oldLat) {
      if (newLat !== oldLat && this.lng) {
        this.getPlaceName(parseFloat(newLat), parseFloat(this.lng));
      }
    },
    lng(newLng, oldLng) {
      if (newLng !== oldLng && this.lat) {
        this.getPlaceName(parseFloat(this.lat), parseFloat(newLng));
      }
    },
  },

  mounted() {
    this.initMapTools();
  },

  beforeDestroy() {
    if (this.autocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  },

  methods: {
    async initMapTools() {
      try {
        await this.$gmapApiPromiseLazy();
        this.$nextTick(() => {
          this.initAutocomplete();
          if (this.lat && this.lng) {
            this.getPlaceName(parseFloat(this.lat), parseFloat(this.lng));
          }
        });
      } catch (error) {
        console.error("Failed to initialize map tools:", error);
      }
    },

    initAutocomplete() {
      const input = this.$refs.searchInput;

      if (!input || !window.google?.maps?.places) {
        return;
      }

      if (this.autocomplete) {
        window.google.maps.event.clearInstanceListeners(this.autocomplete);
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(input, {
        fields: ["formatted_address", "geometry", "name"],
      });

      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();

        if (place.geometry && place.geometry.location) {
          const newLat = place.geometry.location.lat();
          const newLng = place.geometry.location.lng();

          this.$emit("update:lat", newLat);
          this.$emit("update:lng", newLng);
          this.placeName = place.formatted_address || place.name || "";
          this.$emit("update:placeName", this.placeName);
        }
      });
    },

    onMarkerDragEnd(event) {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();

      this.$emit("update:lat", newLat);
      this.$emit("update:lng", newLng);
      this.getPlaceName(newLat, newLng);
    },

    onMapClick(event) {
      const clickedLat = event.latLng.lat();
      const clickedLng = event.latLng.lng();

      this.$emit("update:lat", clickedLat);
      this.$emit("update:lng", clickedLng);
      this.getPlaceName(clickedLat, clickedLng);
    },

    getPlaceName(lat, lng) {
      if (!window.google?.maps?.Geocoder) {
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat, lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.placeName = results[0].formatted_address;
        } else {
          this.placeName = "";
        }

        this.$emit("update:placeName", this.placeName);
      });
    },
  },
};
</script>

<style scoped>
.map_wrapper {
  width: 100%;
  position: relative;
}

.map-search-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  margin-bottom: 10px;
  box-sizing: border-box;
  border: 1px solid var(--border_clr, #dcdcdc);
  border-radius: 8px;
  background: var(--theme_inputs_clr, #fff);
  color: var(--theme_text_clr, #313131);
}

.map-search-input::placeholder {
  color: var(--light_gray_clr, #8d8d8d);
}

.map-search-input:focus {
  outline: none;
  border-color: var(--main_theme_clr, #ef3405);
  box-shadow: 0 0 0 2px rgba(239, 52, 5, 0.1);
}
</style>

<style lang="scss">
.dark_theme .map_wrapper .map-search-input {
  background: #1e1e1e;
  color: #f2f2f2;
  border-color: rgba(239, 52, 5, 0.4);
}

.dark_theme .map_wrapper .map-search-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.dark_theme .map_wrapper .map-search-input:focus {
  border-color: #ff6b42;
  box-shadow: 0 0 0 2px rgba(255, 107, 66, 0.18);
}
</style>
